import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type SuggestionsLandingStateProps = {
  remainingRequests: number | null;
  onAskFim: () => void;
  onViewHistory: () => void;
};

/**
 * Landing state shown when the suggestions modal first opens.
 * Displays the "Ask Fim" button and remaining requests count.
 * Button is disabled when remaining is 0 or unavailable (null).
 */
export function SuggestionsLandingState({
  remainingRequests,
  onAskFim,
  onViewHistory,
}: SuggestionsLandingStateProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const isDark = colorScheme === 'dark';

  const isUnavailable = remainingRequests === null;
  const isDisabled = isUnavailable;

  const themed = themedStyles(isDark, palette);

  return (
    <Animated.View entering={FadeIn.duration(300)} style={styles.container} pointerEvents="box-none">
      <View style={[styles.card, themed.card]}>
        <Ionicons name="sparkles" size={48} color="#FFD700" />

        <Text style={[styles.title, themed.title, { fontFamily: getBodyFont() }]}>
          Ask Fim for suggestions
        </Text>

        <Text style={[styles.description, themed.description, { fontFamily: getBodyFont() }]}>
          Fim will analyze your calendar history and upcoming events to suggest helpful actions for your family.
        </Text>

        <TouchableOpacity
          style={[styles.askButton, { backgroundColor: palette.tint }, isDisabled && styles.askButtonDisabled]}
          onPress={onAskFim}
          disabled={isDisabled}
          activeOpacity={0.8}
        >
          <Ionicons name="sparkles" size={20} color="#FFFFFF" />
          <Text style={[styles.askButtonText, { fontFamily: getBodyFont() }]}>
            Ask Fim
          </Text>
        </TouchableOpacity>

        <StatusText
          remainingRequests={remainingRequests}
          isUnavailable={isUnavailable}
          isDark={isDark}
        />

        <TouchableOpacity
          style={[styles.historyButton, themed.historyButton]}
          onPress={onViewHistory}
          activeOpacity={0.8}
        >
          <Ionicons name="time-outline" size={18} color={isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)'} />
          <Text style={[styles.historyButtonText, themed.historyButtonText, { fontFamily: getBodyFont() }]}>
            Previous suggestions
          </Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

type StatusTextProps = {
  remainingRequests: number | null;
  isUnavailable: boolean;
  isDark: boolean;
};

function StatusText({ remainingRequests, isUnavailable, isDark }: StatusTextProps) {
  const statusColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
  const unavailableColor = isDark ? '#FF6B6B' : '#C83232';

  if (isUnavailable) {
    return (
      <Text style={[styles.statusText, { color: unavailableColor, fontFamily: getBodyFont() }]}>
        Currently unavailable
      </Text>
    );
  }

  return (
    <Text style={[styles.statusText, { color: statusColor, fontFamily: getBodyFont() }]}>
      {remainingRequests} of 3 remaining today
    </Text>
  );
}

function themedStyles(isDark: boolean, palette: typeof Colors.light) {
  return {
    card: {
      backgroundColor: isDark ? palette.cardBackground : 'rgba(255, 255, 255, 0.92)',
    },
    title: {
      color: palette.text,
    },
    description: {
      color: palette.textMuted,
    },
    historyButton: {
      borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
    },
    historyButtonText: {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)',
    },
  };
}

const styles = StyleSheet.create({
  container: {
    // Anchor to the modal edges directly so the card centers against the full
    // screen, not the residual flex space left by sibling elements (header,
    // backdrop, etc.). This makes "center" mean center-of-screen regardless of
    // how the parent overlay distributes flex children. The bottom padding
    // shifts the card upward by ~10% of the viewport so it sits above center.
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.xxl,
    paddingBottom: '20%',
  },
  card: {
    borderRadius: BorderRadius.lg,
    padding: Spacing.xl,
    alignItems: 'center',
    gap: Spacing.md,
    width: '100%',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: Spacing.sm,
  },
  description: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: Spacing.md,
  },
  askButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.md,
    borderRadius: 16,
  },
  askButtonDisabled: {
    opacity: 0.4,
  },
  askButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  statusText: {
    fontSize: 13,
    marginTop: Spacing.xs,
  },
  historyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: Spacing.sm,
  },
  historyButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
